import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { addTransitLayers, attachTransitStops, setTransitStopsLanguage } from './transitStops.js';

// The base map from OpenFreeMap (free vector tiles, no key, no usage limit), drawn with MapLibre inside
// Leaflet. Shared by kartvy (Map.js) and listvy (ListViewPage.js):
// - Positron (light, flat) under the areas, with its labels drawn in a separate map above the areas
//   (OPEN_FREE_MAP_LABEL_PANE) and restyled to read clearly over the colours.
// - Bright with the colours off, and inside a hovered area (areaMapReveal.js).
// - In dark mode with the colours off (Linjer, Ingen, Av): OpenFreeMap's Dark, lifted to a dark grey
//   (darkMapStyle), for the base and the hover reveal. Fyllda keeps the white Positron in dark mode too.
// If OpenFreeMap fails (no WebGL, style or tiles not loading), the view falls back to Stadia, then OSM.

// The map credit, shorter than the styles' own ("OpenFreeMap © OpenMapTiles Data from OpenStreetMap"),
// keeping what their licences ask for.
const OPEN_FREE_MAP_ATTRIBUTION = '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a>'
    + ' © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a>'
    + ' © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

export const OPEN_FREE_MAP_STYLES = {
    positron: 'https://tiles.openfreemap.org/styles/positron',
    bright: 'https://tiles.openfreemap.org/styles/bright',
    // Dark mode with the colours off (Linjer, Ingen, Av), lifted (see darkMapStyle).
    dark: 'https://tiles.openfreemap.org/styles/dark',
};

// Dark mode's map with the colours off (Fyllda keeps the white Positron): OpenFreeMap's Dark with every map
// colour raised by DARK_MAP_LIFT in lightness. As published it is near-black (land rgb 12); lifted it is dark
// grey (land 45): calmer next to the dark interface, the dimming around a search still darkens the rest, and
// the area colours read closer to their real tones. Its labels get light text on dark halos. Done to the style
// JSON before MapLibre draws it, so the unlifted map never shows for a frame.
const DARK_MAP_LIFT = 0.13;
const DARK_MAP_COLOUR_PROPS = ['background-color', 'fill-color', 'line-color', 'fill-outline-color', 'fill-extrusion-color'];
const DARK_MAP_LABEL = { halo: 'rgba(18, 22, 21, 0.85)', place: '#eef3f1', minor: '#c9d3d0', road: '#b8c3c0', water: '#8fb3d9' };

// '#rgb', '#rrggbb', 'rgb()/rgba()' or 'hsl()/hsla()' -> { r, g, b, a }; anything else (expression words,
// property names) -> null, so it is left alone.
const parseCssColour = (value) => {
    const text = value.trim();
    let match = text.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (match) {
        const hex = match[1].length === 3 ? match[1].split('').map((c) => c + c).join('') : match[1];
        return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16), a: 1 };
    }
    match = text.match(/^rgba?\(([^)]*)\)$/i);
    if (match) {
        const [r, g, b, a = 1] = match[1].split(',').map((part) => parseFloat(part));
        return { r, g, b, a };
    }
    match = text.match(/^hsla?\(([^)]*)\)$/i);
    if (match) {
        const [h, s, l, a = 1] = match[1].split(',').map((part) => parseFloat(part));
        const sat = s / 100;
        const light = l / 100;
        const k = (n) => (n + (h / 30)) % 12;
        const f = (n) => 255 * (light - (sat * Math.min(light, 1 - light) * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))));
        return { r: f(0), g: f(8), b: f(4), a };
    }
    return null;
};
const liftColour = ({ r, g, b, a }, delta) => {
    const [R, G, B] = [r / 255, g / 255, b / 255];
    const max = Math.max(R, G, B);
    const min = Math.min(R, G, B);
    let h = 0;
    let s = 0;
    let l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === R) h = ((G - B) / d) + (G < B ? 6 : 0);
        else if (max === G) h = ((B - R) / d) + 2;
        else h = ((R - G) / d) + 4;
        h /= 6;
    }
    l = Math.min(1, l + delta);
    const q = l < 0.5 ? l * (1 + s) : l + s - (l * s);
    const p = (2 * l) - q;
    const channel = (t) => {
        const u = ((t % 1) + 1) % 1;
        if (u < 1 / 6) return p + ((q - p) * 6 * u);
        if (u < 1 / 2) return q;
        if (u < 2 / 3) return p + ((q - p) * ((2 / 3) - u) * 6);
        return p;
    };
    const [r2, g2, b2] = s === 0 ? [l, l, l] : [channel(h + (1 / 3)), channel(h), channel(h - (1 / 3))];
    return `rgba(${Math.round(r2 * 255)}, ${Math.round(g2 * 255)}, ${Math.round(b2 * 255)}, ${Number(a.toFixed(3))})`;
};
// A paint value: a colour string, an expression (arrays holding colours) or a legacy { stops } function.
const liftColours = (value) => {
    if (typeof value === 'string') {
        const colour = parseCssColour(value);
        return colour ? liftColour(colour, DARK_MAP_LIFT) : value;
    }
    if (Array.isArray(value)) return value.map(liftColours);
    if (value && typeof value === 'object' && Array.isArray(value.stops)) {
        return { ...value, stops: value.stops.map(([zoom, stop]) => [zoom, liftColours(stop)]) };
    }
    return value;
};
const darkMapStyle = (json) => {
    json.layers.forEach((layer) => {
        if (layer.type === 'symbol') {
            if (!layer.layout || !layer.layout['text-field']) return;
            const { id } = layer;
            layer.paint = {
                ...(layer.paint || {}),
                'text-halo-color': DARK_MAP_LABEL.halo,
                'text-halo-width': 1.4,
                'text-color': id.startsWith('water_name') ? DARK_MAP_LABEL.water
                    : /^place_(city|town|state|country)/.test(id) ? DARK_MAP_LABEL.place
                        : id.startsWith('highway_name') ? DARK_MAP_LABEL.road
                            : DARK_MAP_LABEL.minor,
            };
            return;
        }
        if (!layer.paint) return;
        DARK_MAP_COLOUR_PROPS.forEach((property) => {
            if (layer.paint[property] !== undefined) layer.paint[property] = liftColours(layer.paint[property]);
        });
    });
    return json;
};
let darkStyleLoad = null;
const loadDarkStyle = () => {
    if (!darkStyleLoad) {
        const timeout = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(15000) } : {};
        darkStyleLoad = fetch(OPEN_FREE_MAP_STYLES.dark, timeout)
            .then((response) => {
                if (!response.ok) throw new Error(`style: HTTP ${response.status}`);
                return response.json();
            })
            .then(darkMapStyle);
        darkStyleLoad.catch(() => { darkStyleLoad = null; });
    }
    // Each map gets its own copy: MapLibre may keep and change the object it is given.
    return darkStyleLoad.then((json) => JSON.parse(JSON.stringify(json)));
};
// What createLayer gets for a style: a URL, or (the dark map) the lifted style object.
const resolveStyle = (style) => (style === 'dark' ? loadDarkStyle() : Promise.resolve(OPEN_FREE_MAP_STYLES[style]));
// z-index 440: above the areas (overlay pane 400) and their outlines (410), below the listing glide
// canvas (450), markers (600) and popups (700).
export const OPEN_FREE_MAP_LABEL_PANE = 'openFreeMapLabelPane';
export const OPEN_FREE_MAP_FALLBACK_SESSION_KEY = 'open_free_map_failed_v2';
// No style after this long (while the tab is visible) counts as a failure.
const LOAD_TIMEOUT_MS = 15000;
// Cap the GL render resolution on high-DPI screens (perf): 1.5 keeps the map crisp but cuts per-frame work.
const OPEN_FREE_MAP_MAX_PIXEL_RATIO = 1.5;
// How far the GL canvas extends past the viewport (0.5 = 50% each side, so a one-step zoom-out stays covered
// and doesn't flash an empty edge; see the createLayer padding below).
const OPEN_FREE_MAP_ZOOM_PADDING = 0.5;
// A failure keeps the backup map this long; after that the next page load tries OpenFreeMap again.
const FALLBACK_REMEMBER_MS = 10 * 60 * 1000;
// Tile failures (network, 5xx) only switch the map when this many come within the window.
const TILE_FAILURE_LIMIT = 8;
const TILE_FAILURE_WINDOW_MS = 30000;

let maplibreLoad = null;
// MapLibre (~800 kB) is only loaded when a map is shown.
export const loadMaplibre = () => {
    if (!maplibreLoad) {
        maplibreLoad = Promise.all([
            import('maplibre-gl'),
            import('maplibre-gl/dist/maplibre-gl.css'),
        ]).then(async () => {
            const plugin = await import('@maplibre/maplibre-gl-leaflet');
            const MaplibreGL = plugin.MaplibreGL || L.MaplibreGL;
            if (typeof MaplibreGL !== 'function') throw new Error('maplibre-gl-leaflet did not load');
            // Reports every reposition, so a clip drawn in the layer's own pixels (the hovered area) can follow.
            const RepositioningLayer = MaplibreGL.extend({
                // Everything that decides what the canvas shows where: its place and size, its transform, and
                // the camera it was drawn with.
                _drawSignature() {
                    const container = this._container;
                    const position = container && container._leaflet_pos;
                    const canvas = this._glMap?._actualCanvas;
                    const centre = this._glMap.getCenter();
                    return [
                        position ? `${position.x},${position.y}` : '',
                        container ? `${container.style.width}x${container.style.height}` : '',
                        canvas ? canvas.style.transform : '',
                        centre.lng, centre.lat, this._glMap.getZoom(), this._glMap.getBearing(), this._glMap.getPitch(),
                    ].join('|');
                },
                // While a finger drags, the canvas moves with the map pane exactly like the areas' canvas. The
                // plugin also re-drew it about 30 times a second, each time snapped to whole pixels while the
                // drag sits between them, so the labels wiggled against the areas on phones. During a drag (and
                // the glide after it) it is only re-drawn when the padding around it (15% of the map) is nearly
                // used up; it catches up when the map stops (moveend).
                getEvents() {
                    const events = MaplibreGL.prototype.getEvents.call(this);
                    return { ...events, moveend: () => this._update() };
                },
                _update(event) {
                    if (!this._map) return;
                    // A drag, or the glide after it.
                    const dragging = Boolean(this._map.dragging?._draggable?._moving || this._map._panAnim?._inProgress);
                    if (event?.type === 'move' && dragging && this._drawnAt && !this._zooming) {
                        const now = this._map.containerPointToLayerPoint([0, 0]);
                        const drift = now.subtract(this._drawnAt);
                        const room = this._map.getSize().multiplyBy(this.options.padding * 0.8);
                        if (Math.abs(drift.x) < room.x && Math.abs(drift.y) < room.y) {
                            this._offset = now;
                            return;
                        }
                    }
                    MaplibreGL.prototype._update.call(this, event);
                    if (!this._zooming) {
                        // The plugin moves the canvas at once but MapLibre only re-draws it on a later frame, so
                        // for a frame the old picture showed at the new spot: a jolt as big as the drift (up to
                        // 12% of the map), seen as the map shaking after a pan. Draw it now, with the move.
                        // A zoom's end comes here up to three times with nothing changed in between (zoomend,
                        // moveend, the transition's end): drawing the same picture again is skipped (each was a
                        // full render of the map; MapLibre's own loop still draws anything new, like a tile).
                        if (this._glMap?.style) {
                            const signature = this._drawSignature();
                            if (signature !== this._drawnSignature) {
                                try {
                                    this._glMap.redraw();
                                    this._drawnSignature = signature;
                                } catch (error) { /* drawn on the next frame instead */ }
                            }
                        }
                        this._drawnAt = this._map.containerPointToLayerPoint([0, 0]);
                        // Reports every reposition, so a clip drawn in the layer's own pixels (the hovered area)
                        // can follow.
                        this.fire('reposition');
                    }
                },
            });
            return { createLayer: (options) => new RepositioningLayer(options) };
        });
        maplibreLoad.catch(() => { maplibreLoad = null; });
    }
    return maplibreLoad;
};

export const openFreeMapFailedThisSession = () => {
    try {
        const failedAt = Number(window.sessionStorage.getItem(OPEN_FREE_MAP_FALLBACK_SESSION_KEY));
        return Number.isFinite(failedAt) && failedAt > 0 && Date.now() - failedAt < FALLBACK_REMEMBER_MS;
    } catch (error) {
        return false;
    }
};

export const rememberOpenFreeMapFailure = () => {
    try {
        window.sessionStorage.setItem(OPEN_FREE_MAP_FALLBACK_SESSION_KEY, String(Date.now()));
    } catch (error) {
        // Not remembered: the next page load tries OpenFreeMap again.
    }
};

const isLabelLayer = (layer) => layer.type === 'symbol';

// Positron's labels are light grey and thin; these read clearly over the area colours.
const strengthenLabels = (glMap) => {
    glMap.getStyle().layers.forEach((layer) => {
        if (!isLabelLayer(layer) || !layer.layout || !layer.layout['text-field']) return;
        const { id } = layer;
        glMap.setPaintProperty(id, 'text-halo-color', '#ffffff');
        glMap.setPaintProperty(id, 'text-halo-width', 1.6);
        glMap.setPaintProperty(id, 'text-halo-blur', 0.2);
        if (id.startsWith('highway-name')) {
            glMap.setPaintProperty(id, 'text-color', '#2f3a38');
            glMap.setLayoutProperty(id, 'text-size', ['interpolate', ['linear'], ['zoom'], 13, 12.5, 15, 14]);
        } else if (id === 'label_other') {
            glMap.setPaintProperty(id, 'text-color', '#1d2b2a');
            glMap.setLayoutProperty(id, 'text-font', ['Noto Sans Bold']);
            glMap.setLayoutProperty(id, 'text-size', ['interpolate', ['linear'], ['zoom'], 8, 10, 12, 12, 15, 13]);
        } else if (/^label_(village|town|city|state|country)/.test(id)) {
            glMap.setPaintProperty(id, 'text-color', '#111816');
            glMap.setLayoutProperty(id, 'text-font', ['Noto Sans Bold']);
        } else if (id.startsWith('water_name')) {
            glMap.setPaintProperty(id, 'text-color', '#34507f');
        }
    });
};

// Buildings are nearly the colour of the land in both styles; a slightly darker fill and a clear outline
// make blocks readable. A fill's outline (fill-outline-color) costs nothing extra to draw.
const BUILDING_PAINT = {
    positron: { building: { 'fill-color': 'rgb(220, 221, 216)', 'fill-outline-color': 'rgb(150, 157, 152)' } },
    bright: {
        building: { 'fill-color': '#e9e0d6', 'fill-outline-color': '#c2b6a9' },
        'building-top': { 'fill-color': '#efe6dc', 'fill-outline-color': '#c2b6a9' },
    },
};

const strengthenBuildings = (glMap, style) => {
    Object.entries(BUILDING_PAINT[style] || {}).forEach(([layerId, paint]) => {
        if (!glMap.getLayer(layerId)) return;
        Object.entries(paint).forEach(([property, value]) => glMap.setPaintProperty(layerId, property, value));
    });
};

// Declutter the map over the area colours: hide road-number shields ("73", "75"), the whole transit POI
// layer (bus/rail/airport icons), one-way arrows and airport labels, and keep only schools among the POIs
// (shops, parks and other amenities are hidden; schools are class "school", which includes förskola/
// preschool). Tunnelbana stations and bus stops are the transit kept, added as layers of their own
// (transitStops.js: addTransitLayers) so no other stops come with them and the look is the same whichever base
// is under them. Runs on every style; a layer a style lacks is skipped.
const HIDE_LAYERS = [
    'highway-shield-non-us', 'highway-shield-us-interstate', 'road_shield_us',
    'poi_transit', 'road_oneway', 'road_oneway_opposite', 'airport',
];
const SCHOOL_POI_LAYERS = ['poi_r1', 'poi_r7', 'poi_r20'];

// layers: which instance this is (showLayers above). Symbols (poi, labels) only render on 'labels' (the
// Positron labels drawn over the colours, Fyllda) and 'all' (the Bright base in Linjer/Ingen); the plain
// 'base' and the hover reveal strip them, so the transit layers are only worth adding on the first two.
export const drawsTransit = (layers) => layers === 'labels' || layers === 'all';
const declutterMap = (glMap, layers) => {
    HIDE_LAYERS.forEach((id) => {
        if (glMap.getLayer(id)) glMap.setLayoutProperty(id, 'visibility', 'none');
    });
    SCHOOL_POI_LAYERS.forEach((id) => {
        if (!glMap.getLayer(id)) return;
        const existing = glMap.getFilter(id);
        const schoolOnly = ['==', ['get', 'class'], 'school'];
        glMap.setFilter(id, existing ? ['all', existing, schoolOnly] : schoolOnly);
    });
    if (drawsTransit(layers)) addTransitLayers(glMap);
};

// layers: 'all', 'base' (no labels) or 'labels' (labels only, transparent elsewhere).
const showLayers = (glMap, layers) => {
    if (layers === 'all') return;
    glMap.getStyle().layers.forEach((layer) => {
        const keep = layers === 'labels' ? isLabelLayer(layer) : !isLabelLayer(layer);
        if (!keep) glMap.setLayoutProperty(layer.id, 'visibility', 'none');
    });
};

/**
 * One OpenFreeMap layer inside a react-leaflet MapContainer.
 * style: 'positron' | 'bright'; layers: 'all' | 'base' | 'labels'; pane: Leaflet pane (default tilePane).
 * strongLabels: restyle the labels (see strengthenLabels). onLayer(layer|null): the Leaflet layer once
 * added (e.g. for areaMapReveal). onFail(): OpenFreeMap couldn't be shown.
 */
export const OpenFreeMapLayer = ({ style, layers = 'all', pane, strongLabels = false, onLayer, onFail }) => {
    const map = useMap();
    const callbacksRef = useRef({ onLayer, onFail });
    callbacksRef.current = { onLayer, onFail };

    useEffect(() => {
        let cancelled = false;
        let layer = null;
        let failed = false;
        let timeoutId = null;
        let detachTransit = null;
        const fail = (reason) => {
            if (cancelled || failed) return;
            failed = true;
            console.warn('OpenFreeMap unavailable, falling back:', reason);
            callbacksRef.current.onFail?.();
        };
        Promise.all([loadMaplibre(), resolveStyle(style)]).then(([{ createLayer }, styleValue]) => {
            if (cancelled) return;
            try {
                layer = createLayer({
                    style: styleValue,
                    interactive: false,
                    // The GL canvas covers the viewport plus this ring. The leaflet plugin only transforms
                    // (doesn't re-render) it during a Leaflet zoom, so a one-step zoom-out reveals ~2× the
                    // view; 0.5 keeps the base map covering it instead of flashing an empty edge.
                    padding: OPEN_FREE_MAP_ZOOM_PADDING,
                    // Only the base layer credits the map; the label and hover layers would repeat it.
                    attributionControl: { customAttribution: layers === 'labels' || pane ? '' : OPEN_FREE_MAP_ATTRIBUTION },
                    ...(pane ? { pane } : {}),
                });
                layer.addTo(map);
            } catch (error) {
                fail(error?.message || 'could not create the map (WebGL?)');
                return;
            }
            const glMap = layer.getMaplibreMap();
            // Perf: MapLibre re-renders the whole vector scene every frame while panning/zooming, so at a
            // device pixel ratio of 2 that is ~2× the pixels. Cap the render resolution — Positron's labels
            // and lines stay crisp enough at 1.5, but it cuts ~40% of the per-frame GPU work on retina.
            try {
                const dpr = window.devicePixelRatio || 1;
                if (dpr > OPEN_FREE_MAP_MAX_PIXEL_RATIO && typeof glMap.setPixelRatio === 'function') {
                    glMap.setPixelRatio(OPEN_FREE_MAP_MAX_PIXEL_RATIO);
                }
            } catch (error) { /* older maplibre without setPixelRatio: keep the default ratio */ }
            // Signal when the base map has finished rendering (camera settled + all tiles loaded), so overlays
            // and markers can wait for it instead of appearing on a still-rendering (blurry) map and then
            // gliding into place as it sharpens. Base layer only (not the labels/hover panes).
            if (!pane && layers !== 'labels') {
                glMap.on('idle', () => { map.__ofmBaseIdle = true; try { map.fire('baseidle'); } catch (error) { /* ignore */ } });
                if (!map.__ofmBusyWired) {
                    map.__ofmBusyWired = true;
                    map.on('movestart zoomstart', () => { map.__ofmBaseIdle = false; });
                }
            }
            // The style has loaded once (MapLibre's 'style.load': the style, before any tiles). Not
            // glMap.isStyleLoaded(): that is false whenever a tile is still loading, so a single failed tile while
            // panning looked like the style failing. And not 'load', the first complete drawing with every tile in
            // view: on a slower or busy computer that took over the 15 s below, most often in Fyllda (three
            // instances fetching tiles), and the view fell back to raster tiles for 10 minutes for no fault of
            // OpenFreeMap's (measured with the CPU slowed 4×: Fyllda fell back in every run, Linjer in none).
            let styleReady = false;
            // Only time spent visible counts: a tab opened in the background may not render (or load) at all.
            let visibleWaitMs = 0;
            const tick = () => {
                if (cancelled || failed || styleReady) return;
                if (!document.hidden) visibleWaitMs += 1000;
                if (visibleWaitMs >= LOAD_TIMEOUT_MS) {
                    fail('timed out loading the style');
                    return;
                }
                timeoutId = window.setTimeout(tick, 1000);
            };
            timeoutId = window.setTimeout(tick, 1000);
            const onStyleReady = () => {
                if (styleReady) return;
                styleReady = true;
                window.clearTimeout(timeoutId);
                if (cancelled) return;
                showLayers(glMap, layers);
                declutterMap(glMap, layers);
                if (layers !== 'labels') strengthenBuildings(glMap, style);
                if (strongLabels) strengthenLabels(glMap);
                // Its bus stops and tunnelbana stations grow on hover and open their lines on a click.
                if (drawsTransit(layers)) detachTransit = attachTransitStops(map, glMap);
                callbacksRef.current.onLayer?.(layer);
            };
            glMap.once('style.load', onStyleReady);
            glMap.once('load', onStyleReady);
            if (glMap.style?._loaded) onStyleReady();
            // The style failing, or many tile failures close together (network, HTTP errors); not a missing
            // icon, an aborted tile or a blip.
            const tileFailureTimes = [];
            glMap.on('error', (event) => {
                const status = Number(event?.error?.status) || 0;
                const message = String(event?.error?.message || '');
                if (/abort/i.test(message) || event?.error?.name === 'AbortError') return;
                if (!styleReady && !event?.tile && !event?.sourceId) {
                    fail(message || `style: HTTP ${status}`);
                } else if (status >= 500 || /Failed to fetch|NetworkError|Load failed/i.test(message)) {
                    const now = Date.now();
                    tileFailureTimes.push(now);
                    while (tileFailureTimes.length && now - tileFailureTimes[0] > TILE_FAILURE_WINDOW_MS) tileFailureTimes.shift();
                    if (tileFailureTimes.length >= TILE_FAILURE_LIMIT) fail(message || `tiles: HTTP ${status}`);
                }
            });
        }).catch((error) => fail(error?.message || 'MapLibre failed to load'));

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
            callbacksRef.current.onLayer?.(null);
            detachTransit?.();
            if (layer) layer.remove();
        };
    }, [layers, map, pane, strongLabels, style]);

    return null;
};

/**
 * The whole OpenFreeMap base for a view: Positron under the areas with its labels on top (labelPane), or
 * Bright with the colours off (hideMap) — the lifted Dark map instead in dark mode (dark); plus that same
 * map (Bright with the colours on) in hoverPane for the hovered area (showHover).
 * onHoverLayer(layer|null) hands the hover layer to areaMapReveal; onFail() switches the view to Stadia.
 * swedish: the language of the bus stop / tunnelbana card (transitStops.js).
 */
// The credit line: no "Leaflet" prefix, and on phones folded into an ⓘ that opens it (mapStyle.css).
const useCompactAttribution = () => {
    const map = useMap();
    useEffect(() => {
        const control = map.attributionControl;
        if (!control) return undefined;
        control.setPrefix(false);
        const container = control.getContainer();
        container.classList.add('is-compact');
        const toggle = (event) => {
            if (event.target.closest('a')) return;
            container.classList.toggle('is-open');
        };
        container.addEventListener('click', toggle);
        L.DomEvent.disableClickPropagation(container);
        return () => {
            container.removeEventListener('click', toggle);
            container.classList.remove('is-compact', 'is-open');
        };
    }, [map]);
};

// While the lifted Dark map is shown the map container takes its land colour (#2d2d2d, mapStyle.css
// .has-dark-base), so the edge a zoom-out uncovers for a moment blends with the map instead of the light map's
// beige.
export const OpenFreeMapBase = ({ swedish = true, ...props }) => {
    useCompactAttribution();
    const map = useMap();
    const darkBase = Boolean(props.dark && props.hideMap);
    useEffect(() => { setTransitStopsLanguage(map, swedish); }, [map, swedish]);
    useEffect(() => {
        const container = map.getContainer();
        container.classList.toggle('has-dark-base', darkBase);
        return () => container.classList.remove('has-dark-base');
    }, [map, darkBase]);
    return <OpenFreeMapLayers {...props} />;
};

// dark (dark mode): the colours on (Fyllda) keep the white Positron with its labels and the Bright hover
// reveal, as in light mode; with the colours off (Linjer, Ingen, Av) the base and the hover reveal are the
// lifted Dark map instead of Bright.
const OpenFreeMapLayers = ({ hideMap, dark = false, labelPane, showHover, hoverPane, onHoverLayer, onFail }) => {
    const linesStyle = dark ? 'dark' : 'bright';
    const baseStyle = hideMap ? linesStyle : 'positron';
    const hoverStyle = hideMap ? linesStyle : 'bright';
    return (
        <>
            <OpenFreeMapLayer
                key={baseStyle}
                style={baseStyle}
                layers={hideMap ? 'all' : 'base'}
                onFail={onFail}
            />
            {!hideMap && labelPane && (
                <OpenFreeMapLayer style="positron" layers="labels" pane={labelPane} strongLabels onFail={onFail} />
            )}
            {showHover && hoverPane && (
                // No labels of its own: the labels above the areas already cover the hovered one.
                <OpenFreeMapLayer
                    key={`hover-${hoverStyle}`}
                    style={hoverStyle}
                    layers="base"
                    pane={hoverPane}
                    onLayer={onHoverLayer}
                    onFail={onFail}
                />
            )}
        </>
    );
};
